type LeaderCallback = (signal: AbortSignal) => Promise<void>;

const CHANNEL = 'coconnect-offline-sync-v1';
const LEASE_KEY = 'coconnect_sync_leader_lease_v1';
const LEASE_MS = 15_000;
const HEARTBEAT_MS = 5_000;
const ELECTION_MS = 3_000;

let channel: BroadcastChannel | null = null;
let stopController: AbortController | null = null;

const localWakeListeners = new Set<(reason: string) => void>();

export interface OfflineSyncStateEvent {
  type: 'COMMAND_ACKNOWLEDGED';
  command_id: string;
  user_id: string;
  resource_type: string;
  resource_id: string;
  server_revision?: string | number | null;
  accepted_at?: string | null;
  at: number;
}

const localSyncStateListeners =
  new Set<(event: OfflineSyncStateEvent) => void>();

function emitLocalWake(reason: string): void {
  localWakeListeners.forEach(listener => {
    try { listener(reason); } catch { /* listener isolation */ }
  });
}

function emitLocalSyncState(
  event: OfflineSyncStateEvent,
): void {
  localSyncStateListeners.forEach(listener => {
    try { listener(event); } catch { /* listener isolation */ }
  });
}

export function notifySyncLeader(reason = 'OUTBOX_CHANGED'): void {
  emitLocalWake(reason);

  try {
    if (!channel && typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL);
    }

    channel?.postMessage({
      type: 'WAKE_SYNC',
      reason,
      at: Date.now(),
    });
  } catch {
    // BroadcastChannel is an optimization only.
  }
}

export function notifySyncState(
  event: Omit<OfflineSyncStateEvent, 'at'> &
    Partial<Pick<OfflineSyncStateEvent, 'at'>>,
): void {
  const message: OfflineSyncStateEvent = {
    ...event,
    at: event.at ?? Date.now(),
  };

  emitLocalSyncState(message);

  try {
    if (!channel && typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL);
    }

    channel?.postMessage(message);
  } catch {
    // Same-tab notification still succeeded.
  }
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

export async function runAsSyncLeader(
  callback: LeaderCallback,
): Promise<() => void> {
  stopController?.abort();

  stopController = new AbortController();
  const signal = stopController.signal;
  const locks = (navigator as any).locks;

  if (locks?.request) {
    void locks.request(
      'coconnect-sync-leader',
      { mode: 'exclusive' },
      async () => {
        if (!signal.aborted) {
          await callback(signal);
        }
      },
    );

    return () => stopController?.abort();
  }

  const owner = crypto.randomUUID();

  const ownsLease = (): boolean => {
    try {
      const current = JSON.parse(
        localStorage.getItem(LEASE_KEY) || 'null',
      );

      return (
        current?.owner === owner &&
        Number(current?.expires_at || 0) > Date.now()
      );
    } catch {
      return false;
    }
  };

  const tryClaim = (): boolean => {
    const now = Date.now();

    try {
      const current = JSON.parse(
        localStorage.getItem(LEASE_KEY) || 'null',
      );

      if (
        current &&
        Number(current.expires_at) >= now &&
        current.owner !== owner
      ) {
        return false;
      }

      localStorage.setItem(
        LEASE_KEY,
        JSON.stringify({
          owner,
          expires_at: now + LEASE_MS,
        }),
      );

      // Re-read after writing. localStorage is not a CAS primitive, so this
      // verification narrows races; server idempotency remains the final guard.
      return ownsLease();
    } catch {
      return false;
    }
  };

  const releaseLease = () => {
    try {
      const current = JSON.parse(
        localStorage.getItem(LEASE_KEY) || 'null',
      );

      if (current?.owner === owner) {
        localStorage.removeItem(LEASE_KEY);
      }
    } catch {
      // Best-effort cleanup.
    }
  };

  void (async () => {
    while (!signal.aborted) {
      if (!tryClaim()) {
        await wait(
          ELECTION_MS + Math.floor(Math.random() * 750),
          signal,
        );
        continue;
      }

      const heartbeat = window.setInterval(() => {
        if (signal.aborted) return;

        try {
          if (!ownsLease()) return;

          localStorage.setItem(
            LEASE_KEY,
            JSON.stringify({
              owner,
              expires_at: Date.now() + LEASE_MS,
            }),
          );
        } catch {
          // Lease renewal failure is tolerated; server idempotency remains guard.
        }
      }, HEARTBEAT_MS);

      try {
        await callback(signal);
      } finally {
        window.clearInterval(heartbeat);
        releaseLease();
      }

      if (!signal.aborted) {
        await wait(ELECTION_MS, signal);
      }
    }
  })();

  return () => {
    stopController?.abort();
    releaseLease();
  };
}

export function subscribeSyncWake(
  callback: (reason: string) => void,
): () => void {
  localWakeListeners.add(callback);

  let listenerChannel: BroadcastChannel | null = null;

  if (typeof BroadcastChannel !== 'undefined') {
    listenerChannel = new BroadcastChannel(CHANNEL);

    listenerChannel.onmessage = event => {
      if (event.data?.type === 'WAKE_SYNC') {
        callback(String(event.data.reason || 'UNKNOWN'));
      }
    };
  }

  return () => {
    localWakeListeners.delete(callback);
    listenerChannel?.close();
  };
}

export function subscribeSyncState(
  callback: (event: OfflineSyncStateEvent) => void,
): () => void {
  localSyncStateListeners.add(callback);

  let listenerChannel: BroadcastChannel | null = null;

  if (typeof BroadcastChannel !== 'undefined') {
    listenerChannel = new BroadcastChannel(CHANNEL);

    listenerChannel.onmessage = event => {
      if (event.data?.type === 'COMMAND_ACKNOWLEDGED') {
        callback(event.data as OfflineSyncStateEvent);
      }
    };
  }

  return () => {
    localSyncStateListeners.delete(callback);
    listenerChannel?.close();
  };
}

